import { useRef, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addDays } from 'date-fns';
import { breakpoints } from 'styles';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { ModalButtonWithForm } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Checkbox,
  Datepicker,
  FormGroup,
  RadioGroup,
  Select,
  Text,
  TextArea,
  UserSelector
} from 'components/common/form';

import type { FormApi } from 'informed';

interface Props {
  markComplete: boolean;
  updateUsers: () => Promise<void>;
}

const FollowUsersToggleCompleteInBatch = ({
  markComplete,
  updateUsers
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      courseCompletionAllowRemoval,
      requireReasonWhenDeletingData
    },
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);
  const [newUsers, setNewUsers] = useState<string[]>([]);

  const handleCompletionTimeChange = (date: number) => {
    if (!formApi.current) return;

    const currentStartDate = formApi.current.getValue(
      'startedAtDate'
    ) as number;
    if (currentStartDate > date) {
      formApi.current.setValue('startedAtDate', date);
    }
  };

  const handleSubmit = async (values: {
    comments: string;
    completionTime: string;
    emails?: string[];
    deleteOptions: 'all' | 'answers' | 'completion';
    deletionReason?: string;
    inactivateOrDelete: 'delete' | 'inactivate';
    includeNonParticipants: boolean;
    languageVersion: string;
    startedAtDate: string;
    sendCertificate?: boolean;
    usernames?: string[];
  }) => {
    try {
      const response = await api.post(
        `/admin/courses/${course.id}/mark-complete`,
        {
          comments: values.comments ? values.comments : '',
          completionTime: values.completionTime || Date.now(),
          deleteOptions: values.deleteOptions,
          includeNonParticipants: values.includeNonParticipants,
          languageVersion: values.languageVersion || course.language,
          markComplete,
          participants: values.emails
            ? values.emails.map((email) => ({ email }))
            : values.usernames.map((username) => ({ username })),
          requestUser: user.id,
          startedAtDate: values.startedAtDate,
          sendCertificate: values.sendCertificate || false,
          ...(values.deletionReason && {
            deletionReason: values.deletionReason
          }),
          ...(values.inactivateOrDelete && {
            inactivateOrDelete: values.inactivateOrDelete
          })
        }
      );

      await updateUsers();

      dispatch.addNotice({
        text: messages.commonSavedSuccessfully,
        type: 'success'
      });

      if (response.data.errors.deniedBecauseNotACourseParticipant.length)
        dispatch.addNotice({
          text: `${
            values.emails
              ? messages.completionManuallyErrorMessage
              : messages.completionManuallyErrorMessageUsernames
          }:<br /> ${response.data.errors.deniedBecauseNotACourseParticipant.join(
            ',<br />'
          )}`,
          type: 'warning'
        });
      if (response.data.errors.deniedBecauseNoAccount.length)
        dispatch.addNotice({
          text: `${
            values.emails
              ? messages.completionManuallyErrorNoAccount
              : messages.completionManuallyErrorNoAccountUsernames
          }:<br /> ${response.data.errors.deniedBecauseNoAccount.join(
            ',<br />'
          )}`,
          type: 'warning'
        });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  let textFields: {
    buttonIcon: string;
    buttonText: string;
    modalTitle: string;
  };
  if (markComplete) {
    textFields = {
      buttonIcon: 'check',
      buttonText: messages.completionManuallyMarkCompleteInBatch,
      modalTitle: messages.completionManuallySetMultiple
    };
  } else {
    textFields = {
      buttonIcon: 'ban',
      buttonText: messages.completionManuallyRemoveInBatch,
      modalTitle: messages.completionManuallyRemoveMultiple
    };
  }

  const availableLanguageOptions = Object.entries(
    getCourseLanguageVersionOptions({ course })
  )
    .filter(([, lang]) => lang.isPublished)
    .map(([key, lang]) => ({ label: lang.capitalizedName, value: key }));

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon={textFields.buttonIcon}
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={textFields.buttonText}
      buttonTextHideBreakpoint={breakpoints.small - 1}
      initialValues={{
        completionTime: dateNow,
        languageVersion: course.language,
        sendCertificate: course.certificateSendToUser,
        startedAtDate: dateNow
      }}
      modalActionText={messages.commonSubmit}
      modalTitle={textFields.modalTitle}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi) => (formApi.current = form)}
    >
      {({ values }) => (
        <>
          <UserSelector
            hideNewUserInformation={true}
            id={`batch-toggle-completion-${Number(markComplete)}`}
            onNewUsers={setNewUsers}
            preventNewAccountsWithUsername={true}
            required={true}
          />

          {accountAddForceExpirationDate && newUsers.length > 0 && (
            <FormGroup
              htmlFor="userExpirationDate"
              label={messages.commonExpirationDateForNewUserAccounts}
              maxWidth="100%"
            >
              <Datepicker
                field="userExpirationDate"
                id="userExpirationDate"
                isClearable={false}
                maxDate={addDays(dateNow, accountAddForceExpirationDateMaxDays)}
                minDate={dateNow}
                required={true}
                style={{ maxWidth: 200 }}
                validateOnChange={true}
                validateOnBlur={true}
                placeholder={messages.commonExpirationDate}
              />
            </FormGroup>
          )}

          {markComplete ? (
            <>
              <FormGroup
                htmlFor="toggle-complete-include-non-participants"
                maxWidth="100%"
              >
                <Checkbox
                  field="includeNonParticipants"
                  id="toggle-complete-include-non-participants"
                  label={
                    <div>
                      {messages.completionManuallyIncludeNonParticipants}
                      <span
                        aria-label={
                          messages.completionManuallyIncludeNonParticipantsInfo
                        }
                        data-tooltip={
                          messages.completionManuallyIncludeNonParticipantsInfo
                        }
                      >
                        <Icon
                          icon="question-circle"
                          style={{ marginLeft: 5 }}
                        />
                      </span>
                    </div>
                  }
                />
              </FormGroup>

              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 20,
                  marginTop: 15
                }}
              >
                <FormGroup
                  htmlFor="completion-time-batch-complete"
                  label={messages.completionsCompletionDate}
                >
                  <Datepicker
                    field="completionTime"
                    id="completion-time-batch-complete"
                    onChange={handleCompletionTimeChange}
                    style={{ width: 160 }}
                  />
                </FormGroup>

                <FormGroup
                  htmlFor="default-started-at-date-batch-complete"
                  label={messages.completionManuallyStartedAtDate}
                  labelTooltip={messages.completionManuallyStartedAtDateTooltip}
                  style={{ marginTop: 0 }}
                >
                  <Datepicker
                    field="startedAtDate"
                    id="default-started-at-date-batch-complete"
                    maxDate={values.completionTime || dateNow}
                    style={{ width: 160 }}
                  />
                </FormGroup>

                {availableLanguageOptions.length > 1 ? (
                  <FormGroup
                    htmlFor="language-version"
                    label={messages.commonLanguage}
                    style={{ marginTop: 0 }}
                  >
                    <Select
                      field="languageVersion"
                      id="language-version"
                      options={availableLanguageOptions}
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                ) : null}
              </div>

              <FormGroup
                htmlFor="comments-batch-complete"
                label={messages.commonComments}
                labelIsOptional={true}
                maxWidth="100%"
              >
                <TextArea
                  autoGrow={true}
                  field="comments"
                  id="comments-batch-complete"
                  placeholder={messages.commonAddComment}
                />
              </FormGroup>

              <FormGroup>
                <Checkbox
                  id="send-ceritificate-email"
                  field="sendCertificate"
                  label={messages.completionsSendCertificateToUser}
                />
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup
                htmlFor={`deleteOptions-batch`}
                label={messages.completionManuallyRemoveOptions}
                maxWidth="100%"
              >
                <RadioGroup
                  id={`deleteOptions-batch`}
                  field="deleteOptions"
                  initialValue="completion"
                  options={[
                    {
                      id: `delete-only-completion`,
                      label: messages.completionManuallyRemoveOnly,
                      value: 'completion'
                    },
                    {
                      id: `delete-answers`,
                      label: messages.completionManuallyRemoveAnswers,
                      value: 'answers'
                    },
                    {
                      id: `delete-all`,
                      label: messages.completionManuallyRemoveAll,
                      value: 'all'
                    }
                  ]}
                />
              </FormGroup>

              {courseCompletionAllowRemoval ? (
                <FormGroup
                  htmlFor={`completion-delete-options`}
                  label={
                    messages.completionManuallyCompletionDeleteOrInactivate
                  }
                  maxWidth="100%"
                >
                  <RadioGroup
                    id={`completion-delete-options`}
                    field="inactivateOrDelete"
                    initialValue="inactivate"
                    options={[
                      {
                        id: `inactivate-completion`,
                        label: messages.completionManuallyCompletionInactivate,
                        tooltip:
                          messages.completionManuallyCompletionInactivateInfo,
                        value: 'inactivate'
                      },
                      {
                        id: `delete-completion`,
                        label: messages.completionManuallyCompletionDelete,
                        value: 'delete'
                      }
                    ]}
                  />
                </FormGroup>
              ) : null}

              {requireReasonWhenDeletingData &&
              values.inactivateOrDelete === 'delete' ? (
                <FormGroup
                  htmlFor="deletionReason-batch"
                  label={messages.commonDeletionReason}
                  maxWidth="100%"
                >
                  <Text
                    field="deletionReason"
                    id="deletionReason-batch"
                    maxLength={255}
                    autocomplete="off"
                    placeholder={messages.commonInputDeletionReason}
                    required={true}
                  />
                </FormGroup>
              ) : null}
            </>
          )}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default FollowUsersToggleCompleteInBatch;
